<template>
  <ValidationObserver ref="observer" v-slot="{ invalid, handleSubmit }">
    <form @submit.prevent="handleSubmit(submitForm)">
      <div class="space-y-8">
        <div class="space-y-4">
          <ValidationProvider rules="required" name="title" tag="div" vid="title">
            <template #default="{ errors }">
              <BaseFormGroup>
                <BaseFormLabel for="title"> Subject </BaseFormLabel>
                <BaseFormInput id="title" v-model="data.title" />
                <BaseFormInlineMessage v-if="errors.length" danger>
                  {{ errors[0] }}
                </BaseFormInlineMessage>
              </BaseFormGroup>
            </template>
          </ValidationProvider>

          <ValidationProvider name="content" tag="div" vid="content">
            <template #default="{ errors }">
              <BaseFormGroup>
                <BaseFormLabel for="content"> Description </BaseFormLabel>
                <BaseFormTextarea id="content" v-model="data.content" />
                <BaseFormInlineMessage v-if="errors.length" danger>
                  {{ errors[0] }}
                </BaseFormInlineMessage>
              </BaseFormGroup>
            </template>
          </ValidationProvider>

          <ValidationProvider rules="required" name="dueAt" tag="div" vid="dueAt">
            <template #default="{ errors }">
              <BaseFormGroup>
                <BaseFormLabel for="dueAt"> Due at </BaseFormLabel>
                <div class="flex items-center">
                  <BaseFormInput id="dueAtDate" type="date" v-model="dueAtDateInput" />
                  <template v-if="data.hasTime">
                    <span>-</span>
                    <BaseFormInput id="dueAtTime" type="time" v-model="dueAtTimeInput" />
                  </template>
                </div>
                <BaseFormInlineMessage v-if="errors.length" danger>
                  {{ errors[0] }}
                </BaseFormInlineMessage>
              </BaseFormGroup>
            </template>
          </ValidationProvider>

          <ValidationProvider name="hasTime" tag="div" vid="hasTime">
            <template #default="{ errors }">
              <BaseFormGroup>
                <label class="inline-flex items-center">
                  <input
                    type="checkbox"
                    v-model="hasTimeInput"
                    :disabled="active"
                    class="form-checkbox"
                  />
                  <span class="ml-2 select-none font-medium text-sm">Add time</span>
                </label>
                <BaseFormInlineMessage v-if="errors.length" danger>
                  {{ errors[0] }}
                </BaseFormInlineMessage>
              </BaseFormGroup>
            </template>
          </ValidationProvider>
        </div>

        <div class="space-x-2">
          <BaseButton primary small :disabled="invalid || active"> Submit </BaseButton>
          <BaseButton
            type="button"
            primary
            small
            outline
            :disabled="active"
            @click="$emit('cancel')"
          >
            Close
          </BaseButton>
        </div>
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import moment from 'moment';
import { HTTP_UNPROCESSABLE_ENTITY } from '@/api/http-response-codes';
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';

export default {
  mixins: [veeValidateErrorsMixin],

  props: {
    action: {
      type: Function,
      required: true,
    },
    task: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      active: false,
      data: null,
    };
  },

  computed: {
    dueAtDateInput: {
      get() {
        return this.data.dueAt ? moment(this.data.dueAt).format('YYYY-MM-DD') : '';
      },
      set(value) {
        const dueAtTime = this.dueAtTimeInput ? this.dueAtTimeInput : '23:59';

        this.data.dueAt = value ? `${value}T${dueAtTime}` : '';
      },
    },
    dueAtTimeInput: {
      get() {
        return this.data.dueAt ? moment(this.data.dueAt).format('HH:mm') : '';
      },
      set(value) {
        const dueAtDate = this.dueAtDateInput ? this.dueAtDateInput : moment().format('YYYY-MM-DD');
        const dueAtTime = value ? value : '23:59';

        this.data.dueAt = `${dueAtDate}T${dueAtTime}`;
      },
    },
    hasTimeInput: {
      get() {
        return this.data.hasTime;
      },
      set(value) {
        if (!value) {
          this.dueAtTimeInput = '23:59';
        }
        this.data.hasTime = value;
      },
    },
  },

  watch: {
    task: 'setData',
  },

  created() {
    this.setData(this.task);
  },

  methods: {
    setData({ title = '', content = '', dueAt = '', hasTime = false, completedAt = '' } = {}) {
      this.data = {
        title,
        content,
        dueAt: dueAt ? moment(dueAt).format('YYYY-MM-DDTHH:mm') : '',
        hasTime,
        completedAt,
      };
    },
    async submitForm() {
      this.active = true;

      try {
        await this.action(this.data);
      } catch (error) {
        if (error.status === HTTP_UNPROCESSABLE_ENTITY) {
          this.onUnprocessableEntityError(error.data.errors);
        }
      }

      this.active = false;
    },
    onUnprocessableEntityError(errors) {
      this.observer = this.$refs.observer;
      this.addServerErrors(errors);
    },
  },
};
</script>
